import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token =
    localStorage.getItem('ivisora_auth_token') ||
    localStorage.getItem('user_token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

/**
 * Pre-flight: check whether the user can start an export.
 *
 * Returns:
 *   { allowed: true, applyWatermark, maxDimension, sourceTemplateId }
 * or
 *   { allowed: false, reason: 'quota_exceeded' | 'downloads_disabled' |
 *                              'unauthenticated' | 'unknown' }
 *
 * NEVER mutates any server-side counters. Counter writes happen only in
 * confirmDownload() after the file is successfully saved on disk.
 *
 * `sessionType` is "shared_session" for free-user shared sessions or
 * "user_copy" for Pro-owned designs derived from a template. Passing
 * null/undefined means "no template attribution" and the gate becomes a
 * no-op (resolves allowed=true with no watermark/quota).
 */
export async function gateDownload({ templateId, format, sessionType }) {
  if (!templateId) {
    // Scratch design with no source template — nothing to gate.
    return { allowed: true, applyWatermark: false, maxDimension: null };
  }
  try {
    const { data } = await axios.post(
      `${API_URL}/api/me/downloads/register`,
      { template_id: templateId, format, session_type: sessionType || null },
      authHeaders(),
    );
    return {
      allowed: true,
      applyWatermark: !!data.apply_watermark,
      maxDimension: data.max_dimension || null,
      sourceTemplateId: templateId,
    };
  } catch (err) {
    const status = err?.response?.status;
    const code = err?.response?.data?.detail?.code;
    if (status === 401) return { allowed: false, reason: 'unauthenticated' };
    if (status === 429 && code === 'quota_exceeded') {
      return { allowed: false, reason: 'quota_exceeded' };
    }
    if (status === 403 && code === 'downloads_disabled') {
      return { allowed: false, reason: 'downloads_disabled' };
    }
    return { allowed: false, reason: 'unknown' };
  }
}

/**
 * Post-flight: record analytics + increment counters AFTER the file has
 * been successfully saved client-side. Best-effort — analytics failure
 * never breaks the user's already-completed download.
 *
 * Returns the server response, or null on failure.
 */
export async function confirmDownload({ templateId, format, sessionType }) {
  if (!templateId) return null;
  try {
    const { data } = await axios.post(
      `${API_URL}/api/me/downloads/confirm`,
      { template_id: templateId, format, session_type: sessionType || null },
      authHeaders(),
    );
    return data;
  } catch {
    return null;
  }
}
